import encodeQR from 'qr';
import decodeQR from 'qr/decode.js';

export const QR_PAYLOAD_VERSION = 1 as const;
export const QR_PAYLOAD_TYPE = 'otem.box' as const;
export const QR_PAYLOAD_MAX_BYTES = 168 as const;

const QR_FILE_MAX_BYTES = 5 * 1024 * 1024;
const QR_IMAGE_MAX_EDGE = 4096;
const QR_IMAGE_MAX_PIXELS = 16_777_216;
const BOX_REFERENCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{2,127}$/;
const PUBLIC_QR_TOKEN_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{21,63}$/;
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const textEncoder = new TextEncoder();

type QrImage = {
  width: number;
  height: number;
  data: Uint8Array | Uint8ClampedArray | number[];
};

type QrPayload = {
  type: typeof QR_PAYLOAD_TYPE;
  version: typeof QR_PAYLOAD_VERSION;
  box: string;
};

function qrError(code: 'QR_BOX_REFERENCE_INVALID' | 'QR_PAYLOAD_INVALID' | 'QR_IMAGE_INVALID') {
  return new Error(code);
}

function isQrError(error: unknown): error is Error {
  return (
    error instanceof Error &&
    ['QR_BOX_REFERENCE_INVALID', 'QR_PAYLOAD_INVALID', 'QR_IMAGE_INVALID'].includes(
      error.message,
    )
  );
}

export function validateBoxReference(value: unknown): string {
  if (typeof value !== 'string' || !BOX_REFERENCE_PATTERN.test(value)) {
    throw qrError('QR_BOX_REFERENCE_INVALID');
  }
  return value;
}

export function validatePublicQrToken(value: unknown): string {
  if (typeof value !== 'string' || !PUBLIC_QR_TOKEN_PATTERN.test(value)) {
    throw new Error('QR_PUBLIC_TOKEN_INVALID');
  }
  return value;
}

export function createPublicQrDataUrl(canonicalUrl: string): string {
  let url: URL;
  try {
    url = new URL(canonicalUrl);
  } catch {
    throw new Error('QR_PUBLIC_URL_INVALID');
  }
  const pathMatch = /^\/b\/([^/]+)$/.exec(url.pathname);
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    !pathMatch ||
    decodeURIComponent(pathMatch[1]) !== validatePublicQrToken(decodeURIComponent(pathMatch[1]))
  ) {
    throw new Error('QR_PUBLIC_URL_INVALID');
  }
  const svg = encodeQR(url.toString(), 'svg', {
    border: 4,
    ecc: 'quartile',
    encoding: 'byte',
    optimize: true,
  });
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function createQrPayload(box: string): string {
  const payload: QrPayload = {
    type: QR_PAYLOAD_TYPE,
    version: QR_PAYLOAD_VERSION,
    box: validateBoxReference(box),
  };
  const serialized = JSON.stringify(payload);
  if (textEncoder.encode(serialized).byteLength > QR_PAYLOAD_MAX_BYTES) {
    throw qrError('QR_PAYLOAD_INVALID');
  }
  return serialized;
}

export function parseQrPayload(payload: string): string {
  if (
    typeof payload !== 'string' ||
    payload.length === 0 ||
    textEncoder.encode(payload).byteLength > QR_PAYLOAD_MAX_BYTES
  ) {
    throw qrError('QR_PAYLOAD_INVALID');
  }

  let candidate: unknown;
  try {
    candidate = JSON.parse(payload);
  } catch {
    throw qrError('QR_PAYLOAD_INVALID');
  }

  if (candidate === null || typeof candidate !== 'object' || Array.isArray(candidate)) {
    throw qrError('QR_PAYLOAD_INVALID');
  }

  const record = candidate as Record<string, unknown>;
  const keys = Object.keys(record);
  if (
    keys.length !== 3 ||
    !keys.includes('type') ||
    !keys.includes('version') ||
    !keys.includes('box') ||
    record.type !== QR_PAYLOAD_TYPE ||
    record.version !== QR_PAYLOAD_VERSION
  ) {
    throw qrError('QR_PAYLOAD_INVALID');
  }

  let box: string;
  try {
    box = validateBoxReference(record.box);
  } catch {
    throw qrError('QR_PAYLOAD_INVALID');
  }

  if (payload !== createQrPayload(box)) {
    throw qrError('QR_PAYLOAD_INVALID');
  }
  return box;
}

export function createQrDataUrl(box: string): string {
  const svg = encodeQR(createQrPayload(box), 'svg', {
    border: 4,
    ecc: 'quartile',
    encoding: 'byte',
    optimize: true,
  });
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function validateQrImage(image: QrImage): void {
  const { width, height, data } = image;
  if (
    !Number.isSafeInteger(width) ||
    !Number.isSafeInteger(height) ||
    width < 1 ||
    height < 1 ||
    width > QR_IMAGE_MAX_EDGE ||
    height > QR_IMAGE_MAX_EDGE ||
    width * height > QR_IMAGE_MAX_PIXELS ||
    (data.length !== width * height * 3 && data.length !== width * height * 4)
  ) {
    throw qrError('QR_IMAGE_INVALID');
  }
}

export function decodeQrImageData(image: QrImage): string {
  validateQrImage(image);
  let payload: string;
  try {
    payload = decodeQR(image);
  } catch {
    throw qrError('QR_IMAGE_INVALID');
  }
  return parseQrPayload(payload);
}

export async function decodeQrFile(file: File): Promise<string> {
  if (
    !(file instanceof File) ||
    file.size < 1 ||
    file.size > QR_FILE_MAX_BYTES ||
    !ACCEPTED_IMAGE_TYPES.has(file.type.toLowerCase()) ||
    typeof globalThis.createImageBitmap !== 'function'
  ) {
    throw qrError('QR_IMAGE_INVALID');
  }

  let bitmap: ImageBitmap | undefined;
  let canvas: HTMLCanvasElement | undefined;
  try {
    bitmap = await globalThis.createImageBitmap(file);
    if (
      bitmap.width < 1 ||
      bitmap.height < 1 ||
      bitmap.width > QR_IMAGE_MAX_EDGE ||
      bitmap.height > QR_IMAGE_MAX_EDGE ||
      bitmap.width * bitmap.height > QR_IMAGE_MAX_PIXELS
    ) {
      throw qrError('QR_IMAGE_INVALID');
    }

    canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) throw qrError('QR_IMAGE_INVALID');
    context.drawImage(bitmap, 0, 0);
    return decodeQrImageData(context.getImageData(0, 0, bitmap.width, bitmap.height));
  } catch (error) {
    if (isQrError(error)) throw error;
    throw qrError('QR_IMAGE_INVALID');
  } finally {
    bitmap?.close();
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
